import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import connectortrxsummaryService from '../services/connectortrxsummaryService';
import connectorService from '../services/connectorService';
import './ConnectorTrxSummaryForm.css';

function ConnectorTrxSummaryForm({ mode = 'create' }) {
  const { id, connectorid } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    connectorid: connectorid || '',
    accountid: '',
    period: '',
    trxtype: '',
    trxcount: '',
    lasttrx: ''
  });

  useEffect(() => {
    const initializeForm = async () => {
      if (connectorid) {
        setFormData(prev => ({ ...prev, connectorid }));
        // Fetch connector to get accountid
        await fetchConnectorAccountId();
      }

      if (mode !== 'create' && id) {
        await fetchTrxSummary();
      }
    };

    initializeForm();
  }, [id, mode, connectorid]);

  const fetchConnectorAccountId = async () => {
    try {
      const response = await connectorService.getConnectorById(connectorid);
      if (response.data && response.data.accountid) {
        setFormData(prev => ({ ...prev, accountid: response.data.accountid }));
      }
    } catch (err) {
      console.error('Error fetching connector accountid:', err);
    }
  };

  const fetchTrxSummary = async () => {
    try {
      setLoading(true);
      const response = await connectortrxsummaryService.getConnectorTrxSummaryById(id);
      if (response.data.summary) {
        setFormData(response.data.summary);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch transaction summary');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    try {
      setLoading(true);
      if (mode === 'create') {
        await connectortrxsummaryService.createConnectorTrxSummary(formData);
      } else if (mode === 'edit') {
        await connectortrxsummaryService.updateConnectorTrxSummary(id, formData);
      }
      navigate(`/connectors/${connectorid}`);
    } catch (err) {
      setError(err.response?.data?.message || `Failed to ${mode} transaction summary`);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this transaction summary?')) {
      return;
    }

    try {
      setLoading(true);
      await connectortrxsummaryService.deleteConnectorTrxSummary(id);
      navigate(`/connectors/${connectorid}`);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete transaction summary');
      setLoading(false);
    }
  };

  if (loading && mode !== 'create') {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="form-container">
      <div className="form-header">
        <h2>
          {mode === 'create' && 'Add Transaction Summary'}
          {mode === 'edit' && 'Edit Transaction Summary'}
          {mode === 'view' && 'Transaction Summary Details'}
        </h2>
        <button
          type="button"
          onClick={() => navigate(`/connectors/${connectorid}`)}
          className="btn-secondary"
        >
          Back to Connector
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      <form onSubmit={handleSubmit}>
        <div className="form-section">
          <h3>Transaction Summary Information</h3>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="period">Period</label>
              <input
                type="text"
                id="period"
                name="period"
                value={formData.period || ''}
                onChange={handleChange}
                disabled={mode === 'view'}
                placeholder="e.g., 2024-01, daily, weekly"
              />
            </div>

            <div className="form-group">
              <label htmlFor="trxtype">Transaction Type</label>
              <input
                type="text"
                id="trxtype"
                name="trxtype"
                value={formData.trxtype || ''}
                onChange={handleChange}
                disabled={mode === 'view'}
                placeholder="e.g., reservation, profile, availability"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="trxcount">Transaction Count</label>
              <input
                type="number"
                id="trxcount"
                name="trxcount"
                value={formData.trxcount || ''}
                onChange={handleChange}
                disabled={mode === 'view'}
                placeholder="0"
              />
            </div>

            <div className="form-group">
              <label htmlFor="lasttrx">Last Transaction</label>
              <input
                type="datetime-local"
                id="lasttrx"
                name="lasttrx"
                value={formData.lasttrx ? new Date(formData.lasttrx).toISOString().slice(0, 16) : ''}
                onChange={handleChange}
                disabled={mode === 'view'}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="accountid">Account ID</label>
              <input
                type="number"
                id="accountid"
                name="accountid"
                value={formData.accountid || ''}
                onChange={handleChange}
                disabled={true}
              />
            </div>

            <div className="form-group">
              <label htmlFor="connectorid">Connector ID</label>
              <input
                type="number"
                id="connectorid"
                name="connectorid"
                value={formData.connectorid || ''}
                onChange={handleChange}
                disabled={true}
              />
            </div>
          </div>
        </div>

        <div className="form-actions">
          {mode === 'view' && (
            <>
              <button
                type="button"
                onClick={() => navigate(`/connector/${connectorid}/trxsummary/${id}/edit`)}
                className="btn-primary"
              >
                Edit
              </button>
              <button
                type="button"
                onClick={handleDelete}
                className="btn-danger"
                disabled={loading}
              >
                Delete
              </button>
            </>
          )}
          {(mode === 'create' || mode === 'edit') && (
            <>
              <button
                type="submit"
                className="btn-primary"
                disabled={loading}
              >
                {loading ? 'Saving...' : (mode === 'create' ? 'Create' : 'Update')}
              </button>
              <button
                type="button"
                onClick={() => navigate(`/connectors/${connectorid}`)}
                className="btn-secondary"
                disabled={loading}
              >
                Cancel
              </button>
            </>
          )}
        </div>
      </form>
    </div>
  );
}

export default ConnectorTrxSummaryForm;
